// Small dependency-free SVG charts for dashboards.

export interface BarSeries {
  key: string;
  label: string;
  color: string;
}

/** Grouped vertical bars with a light grid; `data` rows hold `label` plus one number per series. */
export function BarChart({
  data,
  series,
  height = 180,
}: {
  data: ({ label: string } & Record<string, number | string>)[];
  series: BarSeries[];
  height?: number;
}) {
  const width = 640;
  const pad = { top: 10, right: 8, bottom: 22, left: 30 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.flatMap((d) => series.map((s) => Number(d[s.key] ?? 0))));
  const niceMax = Math.ceil(max / 4) * 4 || 4;
  const groupW = innerW / Math.max(1, data.length);
  const barW = Math.max(3, Math.min(14, (groupW - 6) / series.length));
  const ticks = [0, niceMax / 2, niceMax];
  return (
    <svg
      className="chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={series.map((s) => s.label).join(' and ')}
    >
      {ticks.map((t) => {
        const y = pad.top + innerH - (t / niceMax) * innerH;
        return (
          <g key={t}>
            <line className="grid-line" x1={pad.left} x2={width - pad.right} y1={y} y2={y} />
            <text x={pad.left - 6} y={y + 4} textAnchor="end">
              {t}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const x0 = pad.left + i * groupW + (groupW - barW * series.length) / 2;
        return (
          <g key={d.label}>
            {series.map((s, j) => {
              const v = Number(d[s.key] ?? 0);
              const h = (v / niceMax) * innerH;
              return (
                <rect
                  key={s.key}
                  x={x0 + j * barW}
                  y={pad.top + innerH - h}
                  width={barW - 1}
                  height={Math.max(h, v ? 1 : 0)}
                  rx={2}
                  fill={s.color}
                >
                  <title>{`${d.label}: ${v} ${s.label.toLowerCase()}`}</title>
                </rect>
              );
            })}
            {i % Math.ceil(data.length / 7) === 0 ? (
              <text x={pad.left + i * groupW + groupW / 2} y={height - 6} textAnchor="middle">
                {d.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/** Horizontal share bars (e.g. users by type). */
export function ShareBars({
  items,
}: {
  items: { label: string; value: number; color?: string }[];
}) {
  const total = items.reduce((s, i) => s + i.value, 0) || 1;
  return (
    <div className="stack-sm">
      {items.map((i) => (
        <div key={i.label} className="stack-sm" style={{ gap: 4 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span>{i.label}</span>
            <span className="num muted">
              {i.value} · {Math.round((i.value / total) * 100)}%
            </span>
          </div>
          <div className="meter">
            <span style={{ width: `${(i.value / total) * 100}%`, background: i.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}
